import { HeroEmailCapture } from '../components/marketing/HeroEmailCapture';
import { MarketingSiteLayout } from '../components/marketing/MarketingSiteLayout';
import { ShareBand } from '../components/marketing/ShareBand';
import type { CityPageConfig } from './cities';
import './cityPage.css';

type Faq = { q: string; a: string };

/**
 * The questions a person new to the city actually asks, in the order they ask them. Rendered as
 * plain details/summary AND as FAQPage JSON-LD, so a search result can show the answer inline.
 */
export function cityFaq(city: CityPageConfig): Faq[] {
  return [
    {
      q: 'Is Kynward for dating or for making friends?',
      a: 'Both, and you choose. Every Circle starts with conversation. What you’re open to stays private during the Circle, and anything after it is mutual.',
    },
    {
      q: 'Do I have to meet in person?',
      a: 'No. A Circle is a group chat on your phone. Meeting up is a plan the Circle makes together in the app, if and when it wants to.',
    },
    {
      q: 'How big is a Circle?',
      a: `Four to six people in ${city.name} who picked the same weekly night.`,
    },
    {
      q: `When do Circles form in ${city.name}?`,
      a: `Pick one of four weekly nights — Tuesday or Thursday at 7 PM, or Saturday or Sunday morning — and a Circle forms a few days before it. It ends at a meetup the group picks. In between, Signal Fire brings one introduction at a time.`,
    },
    {
      q: 'What does it cost?',
      a: 'Joining is free. Plus is optional.',
    },
    {
      q: `When does ${city.name} open?`,
      a: `${city.name} is the first city, and it opens in a beta, invite by invite. Join the waitlist and we’ll email you once, when there’s a seat.`,
    },
  ];
}

function faqJsonLd(faq: Faq[]): string {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  });
}

const STEPS = [
  {
    title: 'Join the waitlist for your city',
    body: 'One email, no password yet. Invites go out in small batches so the first Circles can actually form.',
  },
  {
    title: 'Answer a few honest questions',
    body: 'Ten minutes: how you like to talk, what you’d cross town to eat, what you’re into that most people aren’t.',
  },
  {
    title: 'Talk first, then decide together',
    body: 'The Circle talks for a few days in the app. If it wants to meet, it picks a night and a place together. Nobody sees what you’re open to until it’s mutual.',
  },
] as const;

export function CityPage({ city }: { city: CityPageConfig }) {
  const faq = cityFaq(city);

  return (
    <MarketingSiteLayout>
      <article className="city-page">
        <header className="city-hero">
          <p className="city-kicker">{city.name} · Beta</p>
          <h1 className="city-h1">Moved to {city.name} recently?</h1>
          <p className="city-lead">
            We’ll put you in a small group chat with five people who did too. Conversation first, no
            swiping and no open inbox. It stays on your phone until the Circle makes a plan.
          </p>
          <HeroEmailCapture
            source={`${city.slug}-page`}
            citySlug={city.slug}
            inputId={`${city.slug}-waitlist-email`}
          />
        </header>

        <div className="city-content">
          <section className="city-section" aria-labelledby="city-rhythm">
            <h2 id="city-rhythm" className="city-h2">
              Pick your night.
            </h2>
            <p className="city-p">
              A Circle is four to six people in {city.name} who pick the same weekly night —
              Tuesday or Thursday at 7 PM, or Saturday or Sunday morning. It forms a few days
              before, then ends at a meetup the group plans together; nothing is booked for you.
              Signal Fire brings one introduction at a time, every day.
            </p>
          </section>

          <section className="city-section" aria-labelledby="city-how">
            <h2 id="city-how" className="city-h2">
              How it works
            </h2>
            <ol className="city-steps">
              {STEPS.map((step, i) => (
                <li key={step.title} className="city-step">
                  <span className="city-step__n" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="city-step__title">{step.title}</h3>
                    <p className="city-step__body">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="city-section" aria-labelledby="city-venues">
            <h2 id="city-venues" className="city-h2">
              When a Circle decides to meet
            </h2>
            {city.venues.length > 0 ? (
              <ul className="city-venues">
                {city.venues.map((v) => (
                  <li key={v.name} className="city-venue">
                    {v.href ? (
                      <a className="city-link" href={v.href} rel="noopener noreferrer">
                        {v.name}
                      </a>
                    ) : (
                      <span className="city-venue__name">{v.name}</span>
                    )}
                    <span className="city-venue__area"> · {v.area}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="city-p">
                The first {city.name} venues are being lined up. A Circle that wants to meet picks
                its place together; partner venues are suggestions, never assignments.
              </p>
            )}
            <p className="city-p">
              <a className="city-link" href="/partners">
                Own a bar, café or bookshop? Host a Circle →
              </a>
            </p>
          </section>

          <section className="city-section" aria-labelledby="city-waitlist">
            <h2 id="city-waitlist" className="city-h2">
              {city.name} is the first city.
            </h2>
            <p className="city-p">
              The beta is invite-only, and the waitlist is the door. We’ll email you once, when
              there’s a seat.
            </p>
            <HeroEmailCapture
              source={`${city.slug}-page-bottom`}
              citySlug={city.slug}
              inputId={`${city.slug}-waitlist-email-bottom`}
            />
          </section>

          <section className="city-section" aria-labelledby="city-faq">
            <h2 id="city-faq" className="city-h2">
              Questions people in {city.name} ask
            </h2>
            <div className="city-faq">
              {faq.map((f) => (
                <details key={f.q} className="city-faq__item">
                  <summary className="city-faq__q">{f.q}</summary>
                  <p className="city-faq__a">{f.a}</p>
                </details>
              ))}
            </div>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: faqJsonLd(faq) }}
            />
          </section>
        </div>

        <ShareBand />
      </article>
    </MarketingSiteLayout>
  );
}
